import { useEffect, useState } from 'react';
import type { ViewId, ViewModule, ViewType } from './contract.js';

/**
 * LE CHARGEMENT PARESSEUX DU MODULE D'UNE VUE, avec son cache, a UN SEUL
 * endroit.
 *
 * Une vue est un DESCRIPTEUR (`ViewType`) plus un module charge a la demande
 * (`load()` rend `{ Component, Skeleton? }`) : sans cet import differe, le
 * moindre ecran embarquerait les cinq vues du core. Ce hook tient les trois
 * choses que personne ne doit refaire : le chargement, le CACHE par id, et
 * l'echec (nomme, avec la trace console qui rend l'indirection lisible au
 * debug).
 *
 * POURQUOI IL EST SORTI DU `ViewHost`. La CARTE LIEE d'une fiche monte elle
 * aussi une vue du socle (`core.liste` ou `core.tableau`), et elle n'est pas
 * dans la zone 5 : elle ne passe pas par le host. Deux caches auraient charge
 * deux fois le meme module, et surtout deux copies de la meme regle de
 * chargement auraient diverge au premier ajustement. UN cache, UN chemin.
 *
 * Le cache est volontairement un MODULE-LEVEL Map, partage par tous les ecrans
 * et toutes les cartes du document : rebasculer sur une vue deja vue est
 * instantane, et son squelette est alors disponible sans attendre.
 */
const MODULE_CACHE = new Map<ViewId, ViewModule>();

/** Ce que le hook rend : le module s'il est la, l'echec s'il y en a un, et l'attente. */
export interface ViewModuleState {
  /** Le module charge (`Component`, et `Skeleton` s'il en a un), ou `null`. */
  module: ViewModule | null;
  /** L'echec de CE type de vue, ou `null`. Un echec ne colle jamais au suivant. */
  error: Error | null;
  /** Le module est demande et n'est pas encore la (ni echoue). */
  loading: boolean;
}

/**
 * Charge (une fois) le module de `type`, ou rend l'etat d'attente.
 *
 * `type` a `null` est un etat ORDINAIRE (catalogue vide, aucune vue montable) :
 * rien n'est charge, rien n'attend, rien n'echoue.
 */
export function useViewModule(type: ViewType | null | undefined): ViewModuleState {
  const [, bump] = useState(0);
  const [failure, setFailure] = useState<{ id: ViewId; error: Error } | null>(null);
  const id = type?.id ?? null;
  const module = type != null ? (MODULE_CACHE.get(type.id) ?? null) : null;

  useEffect(() => {
    if (type == null) return;
    if (MODULE_CACHE.has(type.id)) return;
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await type.load();
        MODULE_CACHE.set(type.id, loaded);
        if (!cancelled) bump((tick) => tick + 1);
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error(String(cause));
        console.error(`[taki/views] Chargement de la vue ${type.id} impossible.`, error);
        if (!cancelled) setFailure({ id: type.id, error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type]);

  // Changement de vue : l'echec du module precedent ne colle pas a la nouvelle.
  useEffect(() => {
    setFailure((current) => (current != null && current.id !== id ? null : current));
  }, [id]);

  const error = failure != null && failure.id === id ? failure.error : null;
  return { module, error, loading: type != null && module == null && error == null };
}
